import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { Store } from '@ngrx/store';
import {
  selectContactsConfigNext,
  selectContactsList,
  selectGetContactsLoading,
  selectGetMoreUserContactsLoading,
} from '../../../../ngrx/contacts/contacts.selectors';
import { getMoreUserContactsStart, getUserContactsStart } from '../../../../ngrx/contacts/contacts.actions';
import { ContactItem } from '../../../../ngrx/contacts/contacts.interface';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';
import { of, Subscription, Subject } from 'rxjs';
import { NameInitialsPipe } from '../../pipes/name-initials.pipe';
import { BeyAvatarComponent } from '../bey-avatar/bey-avatar.component';
import { BeyInputComponent } from '../bey-input/bey-input.component';
import { NgClass, NgIf, NgFor, AsyncPipe, TitleCasePipe } from '@angular/common';
import { GetElementIdDirective } from '../../directives/get-element-id.directive';
import { FormatPhonePipe } from '../../pipes/format-phone.pipe';
import { HasPermissionDirective } from '../../../auth/directives/has-permission.directive';

@Component({
  selector: 'bey-phone-book',
  templateUrl: './bey-phone-book.component.html',
  styleUrls: ['./bey-phone-book.component.scss'],
  standalone: true,
  imports: [
    NgClass,
    NgIf,
    BeyInputComponent,
    NgFor,
    BeyAvatarComponent,
    AsyncPipe,
    TitleCasePipe,
    NameInitialsPipe,
    FormatPhonePipe,
    GetElementIdDirective,
    HasPermissionDirective,
  ],
})
export class BeyPhoneBookComponent implements OnInit, OnDestroy {
  contacts$: Observable<ContactItem[]>;
  moreContactsIsLoading$: Observable<boolean>;
  hasMoreContacts$: Observable<string>;
  contactsIsLoading: boolean;
  query: string;
  searchTextChanged$ = new Subject<string>();
  subscriptions$: Subscription = new Subscription();

  /**
   * a title that will be shown in the top of the component
   */
  @Input()
  title: string;

  /**
   * boolean value that determines if the searchbar should be displayed or not(it will be displayed anyway after getting more contacts)
   */
  @Input()
  showSearchBarOnMount: boolean = true;

  /**
   * boolean value that determines if the "New" button should be displayed or not
   */
  @Input()
  showPlusSign: boolean = false;

  /**
   * boolean value that determines if the bey-phone-book component exists in phone-book page or not
   */
  @Input()
  fromPhoneBookPage: boolean = false;

  /*****
   * Header text for send to bank use only on web
   */
  @Input()
  header: string = '';

  @Input()
  expectedPermission: Array<string> = [];

  /****
   * Event emitter hooked on the plus sign (New) button
   *
   */
  @Output()
  onNewClick: EventEmitter<null> = new EventEmitter<null>();

  /***
   * Event triggered when no results found
   */
  @Output()
  noContacts: EventEmitter<boolean> = new EventEmitter<boolean>();

  /****
   * Event triggered by clicking on the contact
   */
  @Output()
  onContactClick: EventEmitter<ContactItem> = new EventEmitter<ContactItem>();

  constructor(public router: Router, private store$: Store) {}

  ngOnInit(): void {
    this.subscriptions$.add(
      this.store$.select(selectGetContactsLoading).subscribe((isLoading) => (this.contactsIsLoading = isLoading))
    );

    this.contacts$ = this.store$.select(selectContactsList);
    this.moreContactsIsLoading$ = this.store$.select(selectGetMoreUserContactsLoading);
    this.hasMoreContacts$ = this.store$.select(selectContactsConfigNext);

    this.store$.dispatch(
      getUserContactsStart({
        limit: this.fromPhoneBookPage ? 10 : 7,
      })
    );

    this.subscriptions$.add(
      this.searchTextChanged$
        .pipe(
          debounceTime(1000),
          distinctUntilChanged(),
          tap((query) => this.getFilteredContacts(query))
        )
        .subscribe()
    );

    this.subscriptions$.add(
      this.contacts$.subscribe((contacts) => {
        if (!this.contactsIsLoading) {
          if (!!!contacts?.length) {
            this.noContacts.emit(true);
          } else {
            this.noContacts.emit(false);
          }
        }
      })
    );
  }

  getMore() {
    this.store$.dispatch(getMoreUserContactsStart());
    this.showSearchBarOnMount = true;
  }

  selectContact(contact: ContactItem) {
    this.onContactClick.emit(contact);
  }

  filterContacts(e: Event) {
    const q = e.target?.['value'];
    this.searchTextChanged$.next(q);
  }

  getFilteredContacts(q) {
    //   dispatch get contact with the filter
    //   todo figure out how to implement search on all fields using logical OR
    this.store$.dispatch(
      getUserContactsStart({
        search: q,
      })
    );

    return of(null);
  }

  onPlusSignClick(): void {
    this.onNewClick.emit();
  }

  ngOnDestroy(): void {
    this.subscriptions$.unsubscribe();
  }
}
